import { ReactComponent as Group } from "assets/images/v2/Group.svg";
import { getLoanId, getNhDealApplicationId, setProfileActiveTab } from "features/authSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useLoanSummaryData } from "hooks/useLoanSummaryData";
import { PropertyLocation, useGetLoanSummaryDetailQuery } from "pages/dashboard/home/dashboardService";
import { useEffect, useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LOAN_DETAILS_CARD_ADDRESS_LABEL,
  LOAN_DETAILS_CARD_ERROR_MESSAGE,
  LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL,
  LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL,
  LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL,
  LOAN_DETAILS_CARD_PENDING_STATUS_TEXT as PENDING_STATUS_TEXT,
} from "utilities/Constant";
import styles from "./loan-details-card.module.scss";
import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";

export default function LoanDetailsCard() {
  const uid = useId();
  const isDesktop = useDesktopChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const loanId = useSelector(getLoanId);

  const [titleClassName, setTitleClassName] = useState("label-large-medium");
  const [valueClassName, setValueClassName] = useState("label-medium-medium");
  const { data: loanSummary, isError } = useGetLoanSummaryDetailQuery({ nhDealApplicationId, loanId, persistState: false });
  const { hasLoanSummaryData } = useLoanSummaryData();
  const dispatch = useDispatch();

  const formatPropertyLocation = (propertyLocation?: PropertyLocation): string => {
    if (!propertyLocation) return PENDING_STATUS_TEXT;

    const { addressLine1, addressLine2, cityName, stateCode, postalCode } = propertyLocation;

    const isAddressEmpty = (value?: string) => !value || ["", "tbd", "tba"].includes(value.toLowerCase());
    const isFieldEmpty = (value?: string) => !value || value.trim() === "";

    if (isAddressEmpty(addressLine1) && isAddressEmpty(addressLine2) && isFieldEmpty(cityName) && isFieldEmpty(stateCode) && isFieldEmpty(postalCode)) {
      return PENDING_STATUS_TEXT;
    }

    const parts: string[] = [];

    parts.push(isAddressEmpty(addressLine1) ? PENDING_STATUS_TEXT : addressLine1!);

    if (!isAddressEmpty(addressLine2)) parts.push(addressLine2!);
    if (!isFieldEmpty(cityName)) parts.push(cityName!);

    if (!isFieldEmpty(stateCode) && !isFieldEmpty(postalCode)) {
      parts.push(`${stateCode!} ${postalCode!}`);
    } else if (!isFieldEmpty(stateCode)) {
      parts.push(stateCode!);
    } else if (!isFieldEmpty(postalCode)) {
      parts.push(postalCode!);
    }

    return parts.join(", ");
  };

  const handleViewLoanDetailsClick = () => {
    dispatch(setProfileActiveTab(NavigationIds.LoanDetails));
  };

  useEffect(() => {
    if (isDesktop) {
      setTitleClassName("label-medium-regular");
      setValueClassName("label-large-medium");
    } else {
      setTitleClassName("label-xsmall-regular");
      setValueClassName("label-medium-medium");
    }
  }, [isDesktop]);

  const renderValue = (value: string | number | undefined, idPrefix: string) => {
    const labelId = `${idPrefix}-label-${uid}`;

    if (value === null || value === undefined || value === "") {
      return (
        <div data-testid={`${idPrefix}-value`}>
          <span
            className={valueClassName}
            aria-labelledby={labelId}
            aria-live="polite"
          >
            &nbsp;{PENDING_STATUS_TEXT}
          </span>
        </div>
      );
    }

    return (
      <div data-testid={`${idPrefix}-value`}>
        <span
          className={valueClassName}
          aria-labelledby={labelId}
          aria-live="polite"
        >
          {value}
        </span>
      </div>
    );
  };

  return (
    <div
      className={styles.content}
      data-testid="loan-details-card"
      aria-label="Loan details card"
    >
      <div className={styles.heading} data-testid="loan-details-heading">
        <div className={styles.myLoan}>
                  {isDesktop  && <h5 className={`heading-medium ${styles.myLoanHeading}`} data-testid="loan-title">My Loan</h5>}
                  {!isDesktop && <h6 className={`heading-medium ${styles.myLoanHeading}`} data-testid="loan-title">My Loan</h6>}
        </div>
      </div>

      <div className={styles.loanDetailsCard} data-testid="loan-details-wrapper">
        <div className={isError ? styles.loanDetailsErrorWrapper : styles.loanDetailsWrapper}>
          <div
            className={isError ? styles.loanContentError : styles.loanContent}
            data-testid={isError ? "loan-error-content" : "loan-content"}
          >
            {isError ? (
              <div
                className={styles.errorWrapper}
                role="alert"
                aria-live="assertive"
                data-testid="loan-details-error"
              >
                <Group className={styles.errorIcon} aria-hidden="true" data-testid="loan-error-icon" />
                <div className={titleClassName}>{LOAN_DETAILS_CARD_ERROR_MESSAGE}</div>
              </div>
            ) : (
              <>
                <div className={styles.propertyAddressTitle}>
                  <span
                    className="label-medium-regular"
                    id={`property-address-label-${uid}`}
                    data-testid="property-address-label"
                  >
                    {LOAN_DETAILS_CARD_ADDRESS_LABEL}
                  </span>
                </div>

                <div
                  className={styles.propertyAddressValue}
                  data-testid="property-address-value"
                >
                  <span
                    className="label-large-medium"
                    aria-labelledby={`property-address-label-${uid}`}
                    aria-live="polite"
                  >
                    {formatPropertyLocation(loanSummary?.propertyLocation)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {isError ? (
          <div className={styles.errorPlaceholder} data-testid="error-placeholder"></div>
        ) : (
          <div className={styles.keyValueSectionWrapper} data-testid="loan-key-value-section">

            <div className={`${styles.keyValuePair} ${styles.loanPurpose}`} data-testid="loan-purpose-section">
              <div>
                <span
                  className={titleClassName}
                  id={`loan-purpose-label-${uid}`}
                  data-testid="loan-purpose-label"
                >
                  {LOAN_DETAILS_CARD_LOAN_PURPOSE_LABEL}
                </span>
              </div>

              {renderValue(loanSummary?.loanPurpose, "loan-purpose")}
            </div>

            <div className={styles.keyValuePair} data-testid="loan-amount-section">
              <div>
                <span
                  className={titleClassName}
                  id={`loan-amount-label-${uid}`}
                  data-testid="loan-amount-label"
                >
                  {LOAN_DETAILS_CARD_LOAN_AMOUNT_LABEL}
                </span>
              </div>

              {renderValue(loanSummary?.loanAmount, "loan-amount")}
            </div>

            <div className={styles.keyValuePair} data-testid="loan-number-section">
              <div>
                <span
                  className={titleClassName}
                  id={`loan-number-label-${uid}`}
                  data-testid="loan-number-label"
                >
                  {LOAN_DETAILS_CARD_LOAN_NUMBER_LABEL}
                </span>
              </div>

              {renderValue(loanSummary?.loanNumber, "loan-number")}
            </div>
          </div>
        )}

        {hasLoanSummaryData && (
          <div
            className={isError ? styles.viewDetailsContainerError : styles.viewDetailsContainer}
            data-testid="view-details-container"
          >
               <button className={styles.viewDetailsWrapper} onClick={handleViewLoanDetailsClick} aria-label="View all tasks">
              <div
                className={styles.viewDetails}
                aria-label="View loan details"
                data-testid="view-details">View Details</div>
                </button>
              </div>
           
         )} 
      </div>
    </div>
  );
}
